/**
 * /{ws}/unreads — All unreads (⌘⇧A; core logic/shortcuts): every conversation with unread messages or mentions, in
 * sidebar order, with its newest unread messages (core UnreadsViewModel: author, one line, time, "N more"), "Mark as
 * read" per conversation and for all of them; a row opens the conversation (at its NEW divider).
 */
import { Shell, Shortcuts, Sidebar, UnreadsViewModel } from "@bundle/core";
import { Badge, Button, ComingSoon, Icon, Pressable, ScrollView, Stack, Text, useFormat, useNavigation, useSidebar, useT, useWorkspace } from "@bundle/ui";
import { useEffect, useMemo, useSyncExternalStore } from "react";


export function UnreadsScreen() {
  const t = useT();
  const nav = useNavigation();
  const ws = useWorkspace();
  const sidebar = useSidebar();
  const cache = ws.state.cache;
  const fmt = useFormat();
  const stops = Shortcuts.unreadStops(Shortcuts.conversationOrder(sidebar.state.sections, sidebar.state.collapsed));
  // Holds no subscriptions or timers (fetch results after unmount are dropped), so it needs no dispose.
  const previewVm = useMemo(() => new UnreadsViewModel(ws.vm), [ws.vm]);
  const previews = useSyncExternalStore(previewVm.subscribe, () => previewVm.state.previews);
  // Fetch previews for what's listed, again when a conversation gets new messages.
  const showKey = stops.map((s) => `${s.channelId}:${cache?.channels[s.channelId!]?.lastMessageSeq ?? 0}`).join(",");
  useEffect(() => {
    if (showKey !== "") previewVm.show(showKey.split(",").map((k) => k.slice(0, k.lastIndexOf(":"))));
  }, [previewVm, showKey]);
  const markRead = (channelId: string) => {
    const c = cache?.channels[channelId];
    if (c !== undefined) ws.vm.deps.data.markRead(ws.vm.wsId, c.id, c.lastMessageSeq);
  };
  if (cache === undefined) return <Stack grow testID="unreads-loading" />;
  if (stops.length === 0) return <ComingSoon icon="inbox" title={t("nav.unreads")} body={t("unreads.empty")} />;
  return (
    <Stack grow testID="unreads-screen">
      <Stack direction="horizontal" align="center" justify="between" gap="3" paddingX="8" paddingY="4">
        <Text variant="title2" weight="bold">
          {t("nav.unreads")}
        </Text>
        <Button variant="ghost" size="sm" icon="check" label={t("unreads.markAllRead")} onPress={() => stops.forEach((s) => markRead(s.channelId!))} testID="unreads-mark-all" />
      </Stack>
      <ScrollView>
        <Stack paddingX="5" paddingY="2" gap="1">
          {stops.map((s) => {
            const c = cache.channels[s.channelId!];
            const ms = cache.memberships[s.channelId!];
            const isDm = c?.kind === "dm" || c?.kind === "group_dm";
            const title = c === undefined ? "" : isDm ? Sidebar.channelTitle(cache, c.id) : `#${c.name}`;
            const fresh = c === undefined || ms === undefined ? 0 : Math.max(0, c.lastMessageSeq - ms.lastReadSeq);
            const preview = previews[s.channelId!];
            return (
              <Stack key={s.id} gap="1" paddingY="1" testID={`unreads-group-${s.channelId}`}>
              <Stack direction="horizontal" align="center" justify="between" gap="3" paddingX="3" paddingY="2" radius="md" testID={`unreads-row-${s.channelId}`}>
                <Pressable onPress={() => nav.open(Shell.formatUrl(Shortcuts.stopRoute(s, ws.slug)))} direction="horizontal" grow accessibilityLabel={title} testID={`unreads-open-${s.channelId}`}>
                  <Stack direction="horizontal" align="center" gap="3" grow>
                    <Icon name={isDm ? "message" : c?.kind === "private" ? "lock" : "hash"} size="icon.md" color="text.secondary" />
                    <Stack grow gap="0.5">
                      <Text weight="semibold">{title}</Text>
                      {fresh > 0 ? (
                        <Text variant="footnote" color="text.secondary">
                          {t("unreads.new", { count: fresh })}
                        </Text>
                      ) : null}
                    </Stack>
                    {s.mentions > 0 ? <Badge count={s.mentions} tone="accent" accessibilityLabel={t("unreads.mentions", { count: s.mentions })} /> : null}
                  </Stack>
                </Pressable>
                <Button variant="ghost" size="sm" label={t("unreads.markRead")} onPress={() => markRead(s.channelId!)} testID={`unreads-read-${s.channelId}`} />
              </Stack>
              {preview !== undefined && preview.rows.length > 0 ? (
                <Stack gap="1" paddingX="3" testID={`unreads-preview-${s.channelId}`}>
                  {preview.rows.map((r) => (
                    <Pressable
                      key={r.id}
                      onPress={() => nav.open(Shell.formatUrl({ type: "channel", ws: ws.slug, channelId: s.channelId!, focus: r.id }))}
                      accessibilityLabel={`${r.author}: ${r.text}`}
                      testID={`unreads-message-${r.id}`}
                    >
                      <Stack direction="horizontal" align="start" gap="2" paddingX="8">
                        <Stack grow gap="0.5">
                          <Stack direction="horizontal" align="center" gap="2">
                            <Text variant="footnote" weight="semibold">
                              {r.author}
                            </Text>
                            <Text variant="caption" color="text.tertiary">
                              {fmt.time(r.at)}
                            </Text>
                          </Stack>
                          <Text variant="footnote" color="text.secondary" lines={2}>
                            {r.text}
                          </Text>
                        </Stack>
                      </Stack>
                    </Pressable>
                  ))}
                  {preview.more > 0 ? (
                    <Stack paddingX="8">
                      <Text variant="caption" color="text.tertiary" testID={`unreads-more-${s.channelId}`}>
                        {t("unreads.more", { count: preview.more })}
                      </Text>
                    </Stack>
                  ) : null}
                </Stack>
              ) : null}
              </Stack>
            );
          })}
        </Stack>
      </ScrollView>
    </Stack>
  );
}
